import { Editor, EditorUtils, ProseMirror } from "@progress/kendo-react-editor";
import { Tooltip } from "@progress/kendo-react-tooltip";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { IndexeddbPersistence } from "y-indexeddb";
import { initProseMirrorDoc, yCursorPlugin, ySyncPlugin, yUndoPlugin } from "y-prosemirror";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";
import { setNewTaskVariables } from "../actions/task";
import { setWorkflowCustomVariables, yDocStatus } from "../actions/taskSidebar";
import { DESC_TYPE } from "../components/TaskSidebar/sidebar.constants";
import { useWorkflowContext } from "../components/Workflow/WorkflowContextProvider";
import { label, number, specialCharacter } from "../config";
import envConfig from "../env.config";
import { getWorkflowFormById } from "../shared/services/workflow.services";
import LoaderOverlay from "./LoaderOverlay";
import { updatedSchema } from "./editor-schema/schema";
import { VARIABLE_VALUES, WEBSOCKET_CONFIG } from "./editor.Constant";
import { EditorStateProvider, useEditorContext } from "./editor.Context";
import { createwebsocketUrl, editorIcon, getActiveVariableIds } from "./editor.helper";
import "./editor.scss";
import EditorPopup from "./editorPopup/EditorPopup";
import { useTaskVariableUpdates, useWorkflowVariableUpdates } from "./editorPopup/TaskCustomVariables/hooks/taskCustomVariable.hooks";
import { getProjectVariables } from "../shared/services/projectVariables.service";
import { appendProjectVariablesToEditorVariableList } from "../shared/utils/projectVariablesChipMerge.utils";
import { getTaskVariables } from "./editorPopup/TaskCustomVariables/services/taskCustomVariable.service";
import { getWorkflowDescriptionVariables } from "./editorPopup/TaskCustomVariables/services/workflowDescriptionVariable.service";
import { registerEditorView, unregisterEditorView } from "./editorRegistry";
import { toolbarList } from "./editorSettings/editorToolsSettings";
// Settings for pasting
import { inputRule } from "./editorSettings/inputRuleSettings";
import { keyMapSettings } from "./editorSettings/keyMapSettings";
import { contentStyleSettings, pasteSettings } from "./editorSettings/pasteSettings";
import { addBookmark, navigateToBookmark, removeBookmark } from "./editorTools/bookmarkUtils";
// Editor Utils
import { getDefaultTaskValues } from "../components/Tasks/tasks.service";
import { handleAnchorClick, handleSmartChipClick } from "./editorUtils/clickEvents";
import ContextMenu from "./editorUtils/contextMenu";
import { handleDefaultContextMenu, handleTableContextMenu } from "./editorUtils/contextMenuConfig";
import { handleLinkHover } from "./editorUtils/hoverEvents";
import { handleKeydown } from "./editorUtils/keyDownEvents";
import { insertImagePlugin } from "./upload-image/insertImagePlugin";
import { insertImageFiles } from "./upload-image/upload-image-util";
import { generateRandomHexColor } from "./util";
import { attachBatchedYjsDocUpdates } from "./yjsdebounce";
import store from "../store/index";
const KendoEditor = (props) => {
  return (
    <EditorStateProvider>
      <KendoEditorContent {...props} />
    </EditorStateProvider>
  );
};

const { EditorState, EditorView, Schema } = ProseMirror;
const { imageResizing, tableResizing, pasteCleanup, sanitize, replaceImageSourcesFromRtf, textHighlight } = EditorUtils;

const KendoEditorContent = ({
  desc,
  disableToolbar,
  setDesc,
  viewRef,
  toggleEditable,
  autoSaveKey,
  setUpdateAutoSaveKey,
  toolbar,
  editableRef,
  editorRef,
  editorEditIcon,
  hidePreview,
  acknowledgeMailEditor,
  provider,
  setProvider,
  collab = { value: false },
  taskKey,
  yjsDocRef,
  showEditIcons,
  awarenessRef,
  streamLevel,
  onMount: customOnMount,
  setEditorState,
  formId,
  isFormSettings,
  formProjectId,
  workflowDescriptionProjectId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { mode } = useSelector((state) => state.taskSidebar.task);
  const { isNewTask, newTaskData, task, originalWorkflowVariables, isYdocexists } = useSelector((state) => state.taskSidebar);
  const taskWorkflowDetails = useSelector((state) => state.taskWorkflowDetails);
  const { user } = useSelector((state) => state.auth);
  const workflowContext = useWorkflowContext();
  const {
    visible,
    setVisible,
    hashPopupVisible,
    setHashPopupVisible,
    setUrl,
    setSelectedLink,
    taskInfo,
    setTaskInfo,
    cancelTokenRef,
    setInputPopup,
    smartChipTarget,
    setChipTarget,
    prevKeyRef,
    modeRef,
    setTaskSearchPopup,
    setEditChip,
    setShowEditChip,
    setSmartChipId,
    openImage,
    setOpenImage,
    overallSearchRef,
    setTempChipId,
    tempChipID,
    setEditFrameId,
    taskCustomVariables,
    setTaskCustomVariables,
    taskCustomVariablesRef,
    popupVisible,
    setPopupVisible,
    popupAnchor,
    setPopupAnchor,
    setActiveTab,
  } = useEditorContext();
  const editIcon = useMemo(() => {
    return editorIcon.find((i) => i.id === editorEditIcon);
  }, [editorIcon, editorEditIcon]);
  const { isCloneTask, cloneTask } = useSelector((state) => state.cloneTaskDetails);
  const ydocRef = React.useRef(null);
  const yXmlFragmentRef = React.useRef(null);
  const showEditIframe = modeRef.current === number.ONE || modeRef.current === number.THREE || !editableRef.current;
  const { addTaskCustomVariables, addWorklowDescriptionCustomVariables } = useTaskVariableUpdates();
  const { updateSmartWorkflowVariables } = useWorkflowVariableUpdates();
  const [contextMenuState, setContextMenuState] = useState({
    show: false,
    offset: null,
    table: null,
    view: null,
    row: null,
  });
  const [findReplaceDialog, setFindReplaceDialog] = useState({
    find: false,
    replace: false,
  });
  const [isValueUpdated, setValueUpdated] = React.useState(false);
  const [lockedState, setLockedState] = useState(task.IsTaskLocked ? number.ONE : number.ZERO);
  const [isYjsBound, setIsYjsBound] = useState(false); // Track Yjs binding
  // State to manage loading status
  const [isLoading, setLoading] = useState(false);
  const defaultDesc = "<p> </p>";

  /**
   * Handles task variables initialization based on different contexts:
   * - Workflow description variables for workflow pages
   * - New task variables (with or without cloning)
   * - Existing task variables
   * Optimizes state updates by consolidating repeated operations
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    const initializeTaskVariables = async () => {
      let taskVariablesData = null;
      const editorProjectIdForTask =
        workflowContext?.workflow?.ProjectId ??
        workflowContext?.workflow?.projectId ??
        taskWorkflowDetails?.workflowBasicDetails?.ProjectId ??
        task?.CurrentProject ??
        newTaskData?.project;

      if (isFormSettings && formId) {
        taskVariablesData = { variables: [] };
        try {
          const [response, projectDocs] = await Promise.all([
            getWorkflowFormById({ id: formId }),
            formProjectId ? getProjectVariables(formProjectId, formId) : Promise.resolve(null),
          ]);
          if (response?.aiDescription !== undefined) {
            setDesc(response.aiDescription || "");
          }
          if (formProjectId && projectDocs) {
            taskVariablesData.variables = appendProjectVariablesToEditorVariableList([], projectDocs, true);
          }
        } catch (error) {
          console.error("Error fetching form AI description:", error);
        }
      } else if (taskKey === DESC_TYPE.WF_DESCRIPTION) {
        if (!workflowContext?.workflow?.Id) return;
        const wfProjectId =
          workflowDescriptionProjectId ??
          workflowContext?.workflow?.ProjectId ??
          workflowContext?.workflow?.projectId;
        const wfResponse = await getWorkflowDescriptionVariables({ workflowId: workflowContext.workflow.Id });
        taskVariablesData = wfResponse || { variables: [] };
      } else if (isNewTask) {
        taskVariablesData = await fetchNewTaskVariables();
      } else {
        const taskId = newTaskData?.id || task.taskId;
        if (!taskId) return;
        taskVariablesData = await getTaskVariables({ taskId, projectId: editorProjectIdForTask });
      }
      if (taskVariablesData?.variables) {
        updateVariableStates(taskVariablesData.variables);
        if (isNewTask) {
          setValueUpdated(false);
          setAutoSaveKey();
        }
      }
    };
    initializeTaskVariables();
  }, [
    newTaskData.relatedAssignedId,
    originalWorkflowVariables,
    taskKey,
    isFormSettings,
    formId,
    formProjectId,
    workflowDescriptionProjectId,
    workflowContext?.workflow?.Id,
    workflowContext?.workflow?.ProjectId,
    workflowContext?.workflow?.projectId,
  ]);

  const setAutoSaveKey = () => {
    if(setUpdateAutoSaveKey) {
      setUpdateAutoSaveKey(number.ONE);
    }
  };

  /**
   * Fetches variables for new tasks, handling both workflow and clone scenarios
   * @returns {Promise<Object|null>} Task variables data or null
   * @author Abhishek Dhillon
   */
  const fetchNewTaskVariables = async () => {
    const editorProjectId =
      newTaskData?.project ?? taskWorkflowDetails?.workflowBasicDetails?.ProjectId ?? task?.CurrentProject;
    const workflowId = newTaskData.relatedAssignedType === 5 ? newTaskData.relatedAssignedId : taskWorkflowDetails?.workflowBasicDetails?.Id;
    // Handle clone task scenario
    if (isCloneTask) {
      if (!cloneTask?.taskId) return null;
      return await getTaskVariables({ taskId: cloneTask.taskId, projectId: editorProjectId });
    }
    // Handle workflow task scenario
    if (!workflowId) return null;
    const taskVariablesData = await getWorkflowDescriptionVariables({ workflowId });
    if (!taskVariablesData) return null;
    // Process date variables for workflow tasks
    if (taskVariablesData?.variables?.length) {
      processDateVariables(taskVariablesData.variables);
      // Transform workflow variables for new tasks
      taskVariablesData.variables = updateArrayObjects([...taskVariablesData.variables], "workflowDescriptionVariableId", "taskVariableId");
    }
    return taskVariablesData;
  };

  /**
   * Processes date type variables by setting appropriate default values
   * @param {Array} variables - Array of variables to process
   * @author Abhishek Dhillon
   */
  const processDateVariables = (variables) => {
    variables.forEach((variable) => {
      if (variable?.workflowVariableDetails?.type === "date") {
        variable.variableValue = variable?.variableValue === VARIABLE_VALUES.CURRENT_DATE ? new Date() : null;
      }
    });
  };

  /**
   * Consolidates all variable state updates into a single function
   * @param {Array} variables - Variables to set in state
   * @author Abhishek Dhillon
   */
  const updateVariableStates = (variables) => {
    const variablesCopy = [...variables];
    // Update all variable-related states
    setTaskCustomVariables(variablesCopy);
    taskCustomVariablesRef.current = variablesCopy;
    updateSmartWorkflowVariables(variablesCopy);
    // Dispatch new task variables if applicable
    if (isNewTask) {
      dispatch(setNewTaskVariables(variablesCopy));
    }
  };

  /**
   * Updates array objects by replacing a key with a new key
   * @param {Array} arr - Array of objects to update
   * @param {string} keyToDelete - The key to be replaced
   * @param {string} newKey - The new key name
   * @returns {Array} Updated array with new key names
   * @author Abhishek Dhillon
   */
  const updateArrayObjects = (arr, keyToDelete, newKey) => {
    if (arr) {
      return arr?.map((obj) => {
        if (keyToDelete in obj) {
          obj[newKey] = obj[keyToDelete];
          delete obj[keyToDelete];
        }
        return obj;
      });
    }
  };

  /**
   * Handles workflow description variables initialization when workflow context changes
   * Optimized to avoid redundant operations and includes proper error handling
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (isFormSettings) return;
    if (taskKey === DESC_TYPE.WF_DESCRIPTION) return;

    const initializeWorkflowVariables = async () => {
      if (!workflowContext?.workflow?.Id) return;
      const workflowVariablesData = await getWorkflowDescriptionVariables({
        workflowId: workflowContext.workflow.Id,
      });
      if (workflowVariablesData?.variables) {
        updateVariableStates(workflowVariablesData.variables);
      }
    };
    initializeWorkflowVariables();
  }, [workflowContext?.workflow?.Id, isFormSettings, taskKey]);

  useEffect(() => {
    // Update loading status whenever `isLoading` or `collab` changes
    getLoadingStatus();
  }, [isLoading, collab?.isYdocExists]);

  /**
   * Determines the loading state based on the editor content and collaboration status.
   * If the editor has content, loading is set to false.
   * Otherwise, it checks if a collaborative document exists and has a value.
   */
  const getLoadingStatus = () => {
    // If the editor has content, stop loading
    if (viewRef?.current?.dom?.innerHTML) {
      return setLoading(false);
    }
    if (collab?.value) {
      if (provider?.synced && provider?.wsconnected && Boolean(provider?.wsLastMessageReceived)) {
        return setLoading(false);
      }
    }
    // Check if the collaborative document exists and has a valid value
    const { isYdocExists } = collab;
    setLoading(Boolean(isYdocExists));
  };

  useEffect(() => {
    const fragment = yXmlFragmentRef?.current;
    if (fragment) {
      if (viewRef?.current?.dom?.innerHTML) setLoading(false);
      // Attach the observer
      fragment.observeDeep(handleDeepChange);
      // Cleanup observer on unmount
      return () => {
        fragment.unobserveDeep(handleDeepChange);
        setIsYjsBound(false); // Reset binding state
      };
    }
    return () => {
      if (provider) {
        provider?.disconnect();
      }
      if (collab?.YdocRoom) {
        unregisterEditorView(collab?.YdocRoom);
      }
    };
  }, [yXmlFragmentRef?.current]);

  const handleDeepChange = async (events) => {
    if (!isYjsBound) {
      setIsYjsBound(true); // Yjs binding is complete
    }
    if (viewRef?.current) {
      const newContent = viewRef?.current?.dom?.innerHTML;
      setLoading(false);
      if (events[0]?.transaction?.local && autoSaveKey) {
        debouncedSave();
      }
      setDesc(newContent);
    }
  };

  // Navigate to bookmark effect
  useEffect(() => {
    if (isYjsBound && editorRef?.current?.state?.view) {
      handleNavigateToBookmark();
    }
  }, [isYjsBound, editorRef?.current]);

  const handleNavigateToBookmark = () => {
    const hash = window.location.hash.slice(1); // Get the bookmark ID from the URL
    if (!hash) return;
    const { view } = editorRef.current.state;
    if (!view) return;
    navigateToBookmark(view, hash);
  };

  /**
   * Hook to dispatch an action to get default task values if not added already.
   * @author Shivam Mishra
   */
  useEffect(() => {
    if (!defaultDetails.isAdded) {
      dispatch(getDefaultTaskValues(user.companyId, user.id));
    }
  }, [user.companyId, user.id]);

  /**
   * Observes DOM changes to detect when a link dialog is added and automatically focuses on the input field.
   * Cleans up the observer when the component unmounts.
   * @author Bhavana
   */
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const linkInput = document.getElementById("k-editor-link-url");
          if (linkInput) {
            linkInput.focus();
            /** .k-window.k-editor-window will only apply to the link dialog window, and not to other editor windows*/
            const linkWindow = linkInput.closest(".k-window.k-editor-window");
            if (linkWindow) {
              linkWindow.setAttribute("data-kendo-editor-link", "true");
            }
          }
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [taskCustomVariables]);

  /**
   * Hook to update editor mode.
   * @author Shivam Mishra
   */
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // /**
  //  * Hook to update the editor's content editable state.
  //  * Sets 'contenteditable' attribute of the editor's content element.
  //  * @author Shivam Mishra
  //  */
  useEffect(() => {
    editorRef?.current?._contentElement?.setAttribute("contenteditable", editableRef.current);
  }, [editableRef.current]);

  useEffect(() => {
    approvalHandler();
    if (!task.IsTaskLocked) {
      setLockedState(number.ZERO);
    }
  }, [task.IsTaskLocked]);

  const approvalHandler = () => {
    if ((taskKey === "description" || taskKey === "descriptionPopUp") && task.IsTaskLocked && !lockedState) {
      setDesc(defaultDesc);
      const view = editorRef.current.view;
      if (view) {
        EditorUtils.setHtml(view, defaultDesc);
      }
      setLockedState((prevState) => prevState + number.ONE);
    }
  };

  /**
   * Use Case :
   * 1. When User has created a new Task with some value in description
   * 2. Description in db (desc) is updated but yDoc is not created yet
   * 3. when you again open the task to initialise the value of the content of the yDoc
   * 4. we munually append the content to remain in sync with user data.
   */
  useEffect(() => {
    (async () => {
      if (shouldManuallySync()) {
        const collabView = editorRef?.current?.view;
        if (collabView) {
          EditorUtils.setHtml(collabView, desc ? desc : defaultDesc);
          setLoading(false);
          setValueUpdated(true);
          dispatch(yDocStatus(true));
        }
      }
      if (shouldInsertEmptyParagraph()) {
        insertEmptyParagraph();
        setValueUpdated(true);
      }
    })();
  }, [desc, collab?.isYdocExists, editorRef?.current, isValueUpdated, isYdocexists]);

  const shouldManuallySync = () => {
    return Boolean(
      editorRef?.current &&
        Boolean(desc) &&
        !isValueUpdated &&
        Boolean(isNewTask || !isYdocexists) &&
        checkEditorEditable() &&
        [DESC_TYPE.DESCRIPTION, DESC_TYPE.TASK_DESCRIPTION_POPUP].includes(taskKey)
    );
  };

  /**
   * Determines if an empty paragraph should be inserted.
   */
  const shouldInsertEmptyParagraph = () => {
    return taskKey === label.DOX_TASK_KEY && !collab?.isYdocExists && !isValueUpdated && editorRef?.current;
  };

  /**
   * Inserts an empty paragraph with a space in the editor.
   */
  const insertEmptyParagraph = async () => {
    const { view } = editorRef?.current?.state;
    if (view) {
      const schema = view.state.schema;
      const nodeType = schema.nodes.paragraph;
      // Create a text node with a space inside the paragraph
      const textNode = schema.text(" ");
      const paragraphNode = nodeType.create(null, textNode);
      EditorUtils.insertNode(view, paragraphNode);
      view.focus();
    }
  };

  /**
   * Callback function to handle keydown events.
   * @param {EditorView} view - The editor view.
   * @param {KeyboardEvent} domEvent - The DOM event.
   * @author Shivam Mishra
   */
  const handleKeydownCallback = useCallback(
    (domEvent) => {
      // stops propagation of up and down arrow key to stop navigation .
      if (domEvent.key === "ArrowUp" || domEvent.key === "ArrowDown") domEvent.stopPropagation();
      // prevent default behaviour when editor is read only or update chip only .
      if (modeRef.current === number.ONE || modeRef.current === number.THREE || !editableRef.current) {
        domEvent.preventDefault();
        return;
      }
      handleKeydown(
        domEvent,
        setInputPopup,
        insertNonEditable,
        user.id,
        setChipTarget,
        prevKeyRef,
        smartChipTarget,
        setTaskSearchPopup,
        overallSearchRef,
        setTempChipId,
        setActiveTab,
        taskKey
      );
    },
    [setInputPopup, prevKeyRef.current, taskKey]
  );

  /**
   * Callback function to create input rules for formatting nodes.
   * @param {Object} nodes - The nodes object.
   * @author Shivam Mishra
   */
  const inputRuleCallback = useCallback((nodes) => inputRule(nodes), []);

  /**
   * handles open and closing of image modal
   * @author Shivam Mishra
   */
  const toggleDialogs = () => {
    setOpenImage(!openImage);
  };

  /**
   * useCallback function to handle click event.
   * @param {MouseEvent} event - The click event.
   * @author Shivam Mishra
   */
  const handleClickCallback = useCallback((view, event) => {
    !editableRef.current && handleAnchorClick(event, editableRef, dispatch, history);
    if (modeRef.current === number.ONE || !editableRef.current) {
      event.preventDefault();
      return;
    }
    handleSmartChipClick(
      event,
      editableRef,
      setShowEditChip,
      setSmartChipId,
      setEditChip,
      popupVisible,
      setPopupVisible,
      popupAnchor,
      setPopupAnchor,
      taskKey
    );
  }, []);

  /**
   * useCallback function to handle mouseover event on anchor elements.
   * This function checks if the target is an anchor element, extracts the "tId" parameter from its href attribute,
   * fetches task details using the "tId" if available, and updates the component state accordingly.
   * @param {Event} _ - The event object (not used in the function logic).
   * @param {MouseEvent} domEvent - The mouseover event object.
   */
  const onMouseOver = useCallback(
    async (_, domEvent) => {
      const { target } = domEvent;
      handleLinkHover(
        target,
        cancelTokenRef,
        setSelectedLink,
        setUrl,
        setVisible,
        setTaskInfo,
        setEditFrameId,
        setHashPopupVisible,
        taskKey // Type of Editor
      );
    },
    [visible, hashPopupVisible, taskInfo]
  );

  const onImageInsert = (args) => {
    const { files, view, event } = args;
    const nodeType = view.state.schema.nodes.image;
    const position =
      event.type === "drop"
        ? view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })
        : null;
    insertImageFiles({
      view,
      files,
      nodeType,
      position,
    });
    return files.length > 0;
  };

  /**
   * Prepare Editor according to the task.
   * @param {Event} event
   * @returns Reference to the editor
   */
  const onMount = (event) => {
    const state = event.viewProps.state;
    let { schema } = state;
    const mySchema = updatedSchema(schema);
    const webSocketUrl = createwebsocketUrl(envConfig.BASE_URL.Y3_HTTP_URL);
    if (!collab || !collab?.value || !webSocketUrl) {
      setupNonCollabMode(event, mySchema, desc);
    } else {
      setupCollabMode(event, mySchema, webSocketUrl);
    }
    // Call custom onMount handler if provided
    if (customOnMount) {
      customOnMount(event);
    }
    return viewRef.current;
  };

  /**
   * Creates common DOM event handlers for the editor
   * @param {Object} existingHandlers - Existing event handlers from viewProps
   * @returns {Object} Combined event handlers object
   * @author Abhishek Dhillon
   */
  const createEditorEventHandlers = (existingHandlers = {}) => ({
    ...existingHandlers,
    keydown: (view, domEvent) => handleKeydownCallback(domEvent),
    click: (view, domEvent) => handleClickCallback(view, domEvent),
    mouseover: (view, domEvent) => onMouseOver(view, domEvent),
    contextmenu: (view, domEvent) => handleContextMenu(view, domEvent),
    keyup: (view, domEvent) => handleEditorChange(view, domEvent),
  });

  const setupNonCollabMode = (event, mySchema, desc) => {
    const doc = EditorUtils.createDocument(mySchema, desc);

    viewRef.current = new EditorView(
      { mount: event.dom },
      {
        ...event.viewProps,
        handleDOMEvents: createEditorEventHandlers(event.viewProps.handleDOMEvents),
        state: EditorState.create({
          doc,
          plugins: [
            ...tableResizing(),
            imageResizing(),
            inputRuleCallback(mySchema?.nodes),
            insertImagePlugin(onImageInsert),
            keyMapSettings(taskKey, setFindReplaceDialog),
            ...event.viewProps.state.plugins,
          ],
        }),
      }
    );
    registerEditorView(collab?.YdocRoom, viewRef.current);
  };

  /**
   * Initializes Yjs document and ProseMirror schema.
   */
  const initializeYjsDocument = () => {
    const ydoc = new Y.Doc({ gc: false });
    const permanentUserData = new Y.PermanentUserData(ydoc);
    permanentUserData.setUserMapping(ydoc, ydoc.clientID, user.name);
    const yXmlFragment = ydoc.getXmlFragment("prosemirror");
    ydocRef.current = ydoc;
    yXmlFragmentRef.current = yXmlFragment;
    if (yjsDocRef) yjsDocRef.current = ydoc;
    return { ydoc, yXmlFragment, permanentUserData };
  };

  /**
   * Initializes ProseMirror document.
   */
  const initializeProseMirrorDoc = (yXmlFragment, mySchema) => {
    let { doc, mapping } = initProseMirrorDoc(yXmlFragment, mySchema);
    if ((!doc || doc.childCount === 0) && (isNewTask || !isYdocexists)) {
      const defaultTextNode = mySchema.text(" "); // Add a space inside the paragraph
      const defaultParagraph = mySchema.nodes.paragraph.create(null, defaultTextNode);
      doc = mySchema.nodes.doc.create(null, defaultParagraph);
    }
    return { doc, mapping };
  };

  const initializeIndexedDB = async (ydoc) => {
    const indexedDBProvider = new IndexeddbPersistence(collab?.YdocRoom.toString(), ydoc);
  };

  /**
   * Setup connection event handlers for WebSocket provider
   * @param {WebsocketProvider} wsProvider
   * @param {Event} event
   * @param {Object} mySchema
   * @param {String} webSocketUrl
   * @author Abhishek Dhillon
   */
  const setupConnectionHandlers = (wsProvider) => {
    wsProvider.connect();
    // Low-level WebSocket errors
    wsProvider.on("connection-error", (event) => {
      if (wsProvider.wsUnsuccessfulReconnects >= WEBSOCKET_CONFIG.MAX_RETRIES) {
        editableRef.current = false;
        wsProvider.disconnect();
        handleConnectionStatus(WEBSOCKET_CONFIG.CONNECTION_STATUS.DISCONNECTED);
      }
    });
    wsProvider.on("status", ({ status }) => {
      handleConnectionStatus(status);
      if (status === WEBSOCKET_CONFIG.CONNECTION_STATUS.CONNECTED) {
        editableRef.current = true;
      } else {
        editableRef.current = false;
      }
    });
    wsProvider.on("sync", (isSynced) => {
      editableRef.current = isSynced;
      handleConnectionStatus(isSynced ? WEBSOCKET_CONFIG.CONNECTION_STATUS.CONNECTED : WEBSOCKET_CONFIG.CONNECTION_STATUS.DISCONNECTED);
    });
  };

  const handleConnectionStatus = (status) => {
    if ([DESC_TYPE.TASK_DESCRIPTION_POPUP, label.DOX_TASK_KEY].includes(taskKey)) {
      setEditorState(status);
    }
  };

  /**
   * Setup Editor with Collaboration
   * @param {Event} event
   * @param {Object} mySchema
   * @param {String} webSocketUrl
   * @author Abhishek Dhillon
   */
  const setupCollabMode = (event, mySchema, webSocketUrl) => {
    try {
      const { ydoc, yXmlFragment, permanentUserData } = initializeYjsDocument();
      // initializeIndexedDB(ydoc);
      const wsProvider = createWebSocketProvider(webSocketUrl, ydoc);
      const { doc, mapping } = initializeProseMirrorDoc(yXmlFragment, mySchema);
      const state = event.viewProps.state;
      if (awarenessRef) {
        awarenessRef.current = wsProvider.awareness; // Assign awareness
      }
      setupAwareness(wsProvider.awareness);
      viewRef.current = createEditorView(event, doc, mapping, state, mySchema, wsProvider.awareness, yXmlFragment, permanentUserData);
      setProvider(wsProvider);
    } catch (error) {
      editableRef.current = false;
      handleConnectionStatus(WEBSOCKET_CONFIG.CONNECTION_STATUS.DISCONNECTED);
    }
  };

  /**
   * Prepare web socket url
   * @param {*} webSocketUrl
   * @param {*} ydoc
   * @returns Web socket connection
   */
  const createWebSocketProvider = (webSocketUrl, ydoc) => {
    const paramsType = {
      docId: collab?.YdocRoom?.toString(),
      userId: user.id,
      ...(taskKey === label.DOX_TASK_KEY && { type: "cms" }),
    };
    const provider = new WebsocketProvider(webSocketUrl, collab?.YdocRoom?.toString(), ydoc, {
      connect: false, // prevent auto-retry
      params: paramsType,
    });
    setupConnectionHandlers(provider);
    attachBatchedYjsDocUpdates(provider, {
      debounceMs: WEBSOCKET_CONFIG.DOC_UPDATE_BATCH.DEBOUNCE_MS,
      immediateFlushMinBytes: WEBSOCKET_CONFIG.DOC_UPDATE_BATCH.IMMEDIATE_FLUSH_MIN_BYTES,
    }); 
    return provider;
  };

  /**
   * Create a Editor View
   * @returns New Editor
   * @author Abhishek Dhillon
   */
  const createEditorView = (event, doc, mapping, state, mySchema, awareness, yXmlFragment, permanentUserData) => {
    const collabView = new EditorView(
      { mount: event.dom },
      {
        ...event.viewProps,
        handleDOMEvents: createEditorEventHandlers(event.viewProps.handleDOMEvents),
        state: EditorState.create({
          doc: doc,
          plugins: [
            textHighlight(),
            ySyncPlugin(yXmlFragment, { mapping, permanentUserData: permanentUserData }),
            yCursorPlugin(awareness),
            yUndoPlugin(),
            imageResizing(),
            inputRuleCallback(mySchema?.nodes),
            insertImagePlugin(onImageInsert),
            keyMapSettings(taskKey, setFindReplaceDialog),
            ...tableResizing(),
            ...state.plugins,
          ],
        }),
      }
    );
    registerEditorView(collab?.YdocRoom, collabView);
    return collabView;
  };

  /**
   * Handles context menu for table operations
   * @param {EditorView} view - The editor view
   * @param {MouseEvent} domEvent - The DOM event
   */
  const handleContextMenu = (view, domEvent) => {
    if (editableRef?.current) {
      const target = domEvent.target;
      if (target.tagName === "IMG") {
        return;
      }
      const table = target.closest("table");
      const row = target.closest("tr");
      if (table) {
        handleTableContextMenu(view, domEvent, contextMenuState, setContextMenuState, row);
      } else {
        handleDefaultContextMenu(view, domEvent, contextMenuState, setContextMenuState);
      }
    }
  };

  /**
   * Handles editor change events and processes variables in the content
   * @param {EditorView} view - The editor view instance
   * @param {Event} domEvent - The DOM event that triggered the change
   * @author Abhishek Dhillon
   */
  const handleEditorChange = (view, domEvent) => {
    if ([DESC_TYPE.TASK_DESCRIPTION_POPUP, DESC_TYPE.DESCRIPTION, DESC_TYPE.WF_DESCRIPTION].includes(taskKey)) {
      const html = EditorUtils.getHtml(view.state);
      handleVariables(html);
    }
  };

  /**
   * Prepare a list of active users on the editor
   * @param {Object} awareness
   */
  const setupAwareness = (awareness) => {
    awareness.setLocalStateField("user", {
      name: user.name,
      email: user.email,
      color: generateRandomHexColor(),
    });
  };

  /**
   * function to update a non-editable node with specified text and ID into the editor.
   * @param {string} text - The text content of the non-editable node.
   * @param {string} id - The ID of the non-editable node.
   * @author Shivam Mishra
   */
  const updateNonEditable = (text, newNodeId, chipClass = "") => {
    if (editorRef.current) {
      const { view } = editorRef.current.state;
      const { state, dispatch } = view;
      const { schema, doc, tr } = state;
      if (chipClass === "Bookmark") {
        addBookmark(view);
      } else {
        // Traverse through all the nodes in the document
        doc.descendants((node, pos) => {
          if (node.type.name === "nonEditable" && node.attrs.id === tempChipID) {
            // Create a new node with updated attributes or content
            const updatedNode = schema.nodes.nonEditable.create(
              {
                ...node.attrs,
                id: newNodeId,
                class: chipClass,
              },
              schema.text(text)
            );
            // Replace the old node with the new node, then insert a trailing space so typing continues after the chip
            const transaction = tr
              .replaceWith(pos, pos + node.nodeSize, updatedNode)
              .insert(pos + updatedNode.nodeSize, schema.text(specialCharacter.SPACE));
            dispatch(transaction);
          }
        });
        view.focus();
        setInputPopup(false);
      }
    }
  };

  /**
   * useCallback function to insert a non-editable node with specified text and ID into the editor.
   * @param {string} text - The text content of the non-editable node.
   * @param {string} id - The ID of the non-editable node.
   * @author Shivam Mishra
   */
  const insertNonEditable = useCallback(async (text, id, chipClass = "shortcode") => {
    const { view } = editorRef.current.state;
    const schema = view.state.schema;
    // Get the new node from the schema
    const nodeType = schema.nodes.nonEditable;
    // Create a new node with the selected text
    const node = await nodeType.createAndFill({ class: chipClass, id: id }, schema.text(text));
    // Insert the new node
    await EditorUtils.insertNode(view, node);
    await view.focus();
    setInputPopup(false);
    return true;
  }, []);

  /**
   * Debounced update task api call
   * @param {Callback}
   * @returns {Void}
   * @authpr Shivam Mishra
   */
  const debouncedSave = useCallback(
    _.debounce(() => {
      if(setUpdateAutoSaveKey) {
        setUpdateAutoSaveKey((prevKey) => prevKey + number.ONE);
      }
    }, number.FIVE_HUNDRED),
    [autoSaveKey]
  );

  /**
   * Helper function to extract plain text from HTML
   */
  const getPlainTextFromHtml = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  /**
   * Helper function to count words in text
   * @param {string} callText - Text to count words in
   * @returns {number} Word count
   */
  const getWordCount = (text) => {
    if (!text || typeof text !== "string") return 0;
    const words = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    return words.length;
  };

  /**
   * useCallback function to handle change event.
   * @param {Object} event - The change event object.
   * @authpr Shivam Mishra
   */
  const handleChange = useCallback((event) => {
    const nextValue = event.html;
    if (isFormSettings) {
      const plainText = getPlainTextFromHtml(nextValue);
      const wordCount = getWordCount(plainText);
      if (wordCount > 500) {
        return;
      }
    }

    setDesc(nextValue);
    autoSaveKey && debouncedSave();
  }, []);

  const handleWrapperClick = (ev) => {
    const tr = editorRef?.current?.state?.view?.state?.tr;
    const textBeforeCursor = tr?.doc?.textBetween(tr.selection.from - 1, tr.selection.from);
    prevKeyRef.current = textBeforeCursor;
  };

  /**
   * urlify text on enter key press
   * Params {*} event
   * @author Shivam Mishra
   */
  const onKeyDown = useCallback((ev) => {
    if (ev.key === "Enter") {
      if (document.getElementById("k-editor-link-url") && document.getElementById("k-editor-link-text")) {
        ev.preventDefault();
        const insertButton = document.querySelector(".k-button-solid-primary");
        if (insertButton) {
          insertButton.click();
        }
      }
    }
    if (ev.key === "Backspace") {
      handleWrapperClick();
    }
  }, []);

  /**
   * Handles HTML paste events in the editor
   * @param {Object} event - Paste event object
   * @returns {string} Processed HTML content
   * @author Abhishek Dhillon
   */
  const handlePasteHtml = React.useCallback(
    (event) => {
      let html = pasteCleanup(sanitize(event.pastedHtml), pasteSettings);
      if (event.nativeEvent.clipboardData) {
        html = replaceImageSourcesFromRtf(html, event.nativeEvent.clipboardData);
      }
      return replaceSpanTimestamps(html, taskCustomVariables);
    },
    [taskCustomVariables]
  );

  /**
   * Updates task custom variables with new IDs
   * @param {string} oldId - Original variable ID
   * @param {string} newId - New variable ID
   * @returns {Array} Updated task custom variables
   * @author Abhishek Dhillon
   */
  const updateTaskCustomVariables = (variables, oldId, newId) => {
    const taskCustomVariable = variables.find((variable) => variable.taskVariableId === oldId);
    if (taskCustomVariable) {
      return {
        ...taskCustomVariable,
        taskVariableId: newId,
      };
    }
    return null;
  };

  /**
   * Replaces the timestamp in span IDs with current timestamp while maintaining the ID structure
   * @param {string} html - HTML content containing spans
   * @returns {string} HTML content with updated span IDs
   * @author Abhishek Dhillon
   */
  const replaceSpanTimestamps = (html, taskCustomVariables) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const spans = doc.querySelectorAll('span[id^="ss_"]');
    const newTaskCustomVariables = [];
    spans.forEach((span, key) => {
      const currentId = span.id;
      const idParts = currentId.match(/^(ss_\d+_)(\d+)(_.*)$/);
      if (idParts) {
        const spanId = `${idParts[1]}${Date.now() + key}${idParts[3]}`;
        span.id = spanId;
        const updatedVariable = updateTaskCustomVariables(taskCustomVariables, currentId, spanId);
        // if (updatedVariable?.workflowVariableDetails?.chipType === "smart chip") {
        //   span.removeAttribute("id");
        //   span.removeAttribute("class");
        // } else {
          if (updatedVariable) {
            newTaskCustomVariables.push(updatedVariable);
          }
        // }
      }
    });
    if (newTaskCustomVariables.length > 0) {
      setTaskCustomVariables([...taskCustomVariables, ...newTaskCustomVariables]);
      addTaskCustomVariables([...taskCustomVariables, ...newTaskCustomVariables]);
    }
    return doc.body.innerHTML;
  };

  /**
   * Deletes a bookmark from the editor and hides the popup
   * @param {string} bookmarkId - The ID of the bookmark to delete
   * @author Abhishek Dhillon
   */
  const deleteBookmark = (bookmarkId) => {
    const { view } = editorRef.current.state;
    removeBookmark(view, bookmarkId);
    setPopupVisible(false);
  };

  /**
   * Updates workflow variables based on the current context
   * @param {Array} updatedVariables - Array of updated variables
   * @author Abhishek Dhillon
   */
  const updateWorkflowVariables = useCallback(
    (updatedVariables) => {
      if (!Array.isArray(updatedVariables)) return;

      try {
        if (workflowContext?.workflow?.Id) {
          addWorklowDescriptionCustomVariables(updatedVariables, workflowContext?.workflow);
        } else {
          workflowContext?.setworkflowDescriptionVariables(updatedVariables);
        }
      } catch {
        // Silent error handling - maintain current state
        setTaskCustomVariables(taskCustomVariables);
      }
    },
    [workflowContext?.workflow, taskCustomVariables]
  );

  /**
   * Updates task variables based on the current context
   * @param {Array} updatedVariables - Array of updated variables
   * @author Abhishek Dhillon
   */
  const updateTaskVariables = useCallback(
    (updatedVariables) => {
      if (!Array.isArray(updatedVariables)) return;

      try {
        if (!isNewTask) {
          addTaskCustomVariables(updatedVariables);
        } else {
          dispatch(setNewTaskVariables(updatedVariables));
        }
      } catch {
        // Silent error handling - maintain current state
        setTaskCustomVariables(taskCustomVariables);
      }
    },
    [isNewTask, dispatch, taskCustomVariables]
  );

  /**
   * Handles tracking and updating of task variables based on spans in editor content
   * This function is throttled to handle rapid transactions efficiently
   * @author Abhishek Dhillon
   * @returns {void}
   */
  const handleVariables = (html) => {
    if (!editorRef?.current?.state?.view || !Array.isArray(taskCustomVariables)) return;
    try {
      const activeVariableIds = getActiveVariableIds(html);
      const isWorkflowDescription = taskKey === DESC_TYPE.WF_DESCRIPTION;
      const variableIdKey = isWorkflowDescription ? "workflowDescriptionVariableId" : "taskVariableId";
      const updatedVariables = taskCustomVariablesRef.current.filter((v) => activeVariableIds.has(v[variableIdKey]));

      // Only update if variables have actually changed
      const hasVariablesChanged =
        updatedVariables.length !== taskCustomVariablesRef.current.length ||
        !updatedVariables.every((variable, index) => {
          const currentVariable = taskCustomVariablesRef.current[index];
          return currentVariable && variable[variableIdKey] === currentVariable[variableIdKey];
        });

      if (hasVariablesChanged) {
        setTaskCustomVariables(updatedVariables);
        taskCustomVariablesRef.current = [...updatedVariables];
        updateSmartWorkflowVariables([...updatedVariables]);
        if (isWorkflowDescription) {
          updateWorkflowVariables(updatedVariables);
        } else {
          updateTaskVariables(updatedVariables);
        }
      }
    } catch (error) {
      setTaskCustomVariables(taskCustomVariablesRef.current);
    }
  };

  /**
   * Controls whether editor commands execute.
   * Prevents execution when not editable, not in edit mode, or toolbar is disabled.
   * @author Abhishek Dhillon
   * @parent KendoEditor
   */
  const handleEditorExecute = (event) => {
    const isEditable = checkEditorEditable();
    return isEditable;
  };

  const checkEditorEditable = () => {
    if ([DESC_TYPE.TASK_DESCRIPTION_POPUP, DESC_TYPE.DESCRIPTION, DESC_TYPE.APPROVAL_DESCRIPTION].includes(taskKey)) {
      if (task && Boolean(task?.IsTaskComplete || task?.IsTaskClosed)) {
        return false;
      }
    }
    if (editableRef.current) {
      return true;
    }
    if (collab?.value) {
      return provider?.synced && provider?.wsconnected && Boolean(provider?.wsLastMessageReceived);
    }
    return false;
  };

  return (
    <div
      className={`dt-kendo-editor ${
        streamLevel === label.PROJECT || streamLevel === label.QUEUE_STREAM ? "full-height-editor" : ""
      } position-relative overflow-auto`}>
        {/* might be used later */}
      {/* <Popup show={findReplaceDialog.find || findReplaceDialog.replace} className={"p-0"} offset={{ left: 950 }}>
        <FindAndReplace findReplaceDialog={findReplaceDialog} setFindReplaceDialog={setFindReplaceDialog} editorRef={editorRef} desc={desc} />
      </Popup> */}
      {isLoading ? (
        <LoaderOverlay loading={isLoading} loaderClass={"loaderOverlay"} type='converging-spinner' taskKey={taskKey} />
      ) : (
        <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
          <div onClick={handleWrapperClick} onKeyDown={onKeyDown}>
            <Editor
              autoFocus={true}
              tools={checkEditorEditable() ? toolbarList(toolbar) : []}
              onChange={!collab?.value && !disableToolbar ? handleChange : undefined}
              value={!collab?.value ? desc : undefined}
              defaultEditMode='div'
              onMount={onMount}
              ref={editorRef}
              onPasteHtml={(event) => handlePasteHtml(event)}
              className={!checkEditorEditable() || (mode && mode !== number.TWO) || disableToolbar ? "disable-toolbar" : ""}
              contentStyle={contentStyleSettings(taskKey)}
              onExecute={handleEditorExecute}
            />
          </div>
        </Tooltip>
      )}
      {contextMenuState.show && (
        <ContextMenu
          state={contextMenuState}
          setState={setContextMenuState}
          viewProps={{
            setInputPopup,
            setChipTarget,
            setTempChipId,
            setTaskSearchPopup,
            overallSearchRef,
            insertNonEditable,
            userId: user.id,
            editorRef,
            setActiveTab
          }}
        />
      )}
      {!hidePreview && (
        <EditorPopup
          insertNonEditable={insertNonEditable}
          updateNonEditable={updateNonEditable}
          editorRef={editorRef}
          toggleDialogs={toggleDialogs}
          editableRef={editableRef}
          acknowledgeMailEditor={acknowledgeMailEditor}
          showEditIframe={showEditIframe}
          deleteBookmark={deleteBookmark}
          taskKey={taskKey}
          workflowDescriptionProjectId={workflowDescriptionProjectId}
        />
      )}
      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
        {showEditIcons && (
          <div className={` button-align-top  position-absolute button-align`}>
            <button title={editIcon?.title} className={` description-button  mr-2 p-0 rounded-circle `} onClick={() => toggleEditable(editIcon?.id)}>
              <span className={editIcon?.class}>{editIcon?.icon}</span>
            </button>
          </div>
        )}
      </Tooltip>
    </div>
  );
};

export default React.memo(KendoEditor);
